import { getConfig } from '@local/app-config';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useReducer, useState } from 'react';

import {
    clearCustomGcpServiceValuesFor,
    getCustomGcpServiceDisplayLabels,
    getCustomGcpServiceValues,
    isValidCustomGcpServiceValue,
    resolveCustomGcpServiceBaseUrl,
    setCustomGcpServiceValues,
} from '../store/localStorageHelpers/customGcpServicesHelper';
import {
    EVO_INSTANCES_CHANGED_EVENT,
    FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE,
    getAvailableEvoInstances,
    getCurrentEvoInstance,
    INSTANCE_OVERRIDE_DEV_HUB_CODES,
    INSTANCE_OVERRIDE_IDS,
    isInstanceOverrideCode,
    isInstanceOverrideId,
    setCurrentEvoInstance,
    setCustomGcpServicesDisplayLabel,
    setCustomGcpServicesEnabled,
    setEnabledInstanceOverrideIds,
    setLastUsedInstance,
    type EvoInstance,
    type EvoInstances,
    type InstanceOverrideId,
} from '../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import {
    ServiceInstanceDialog,
    type ServiceInstanceDefinition,
    type ServiceInstanceId,
    type ServiceInstanceValues,
} from './ServiceInstanceDialog';

export type InstanceOverrideDefinition =
    | { readonly type: InstanceOverrideId }
    | {
          readonly type: 'custom-gcp-services';
          readonly serviceIds: readonly ServiceInstanceId[];
      };

export interface InstanceOverridesProviderProps extends PropsWithChildren {
    readonly overrides: readonly InstanceOverrideDefinition[];
    readonly onInstanceChange?: (instance: EvoInstance) => void;
    readonly onConfigurationChanged?: () => void;
}

interface InstanceOverridesContextValue {
    enabled: boolean;
    customGcpServicesEnabled: boolean;
    secondaryTextLines: readonly string[];
    openCustomGcpServices: (instance?: EvoInstance) => void;
    selectOverrideInstance: (instance: EvoInstance) => void;
}

const EMPTY_CONTEXT: InstanceOverridesContextValue = {
    enabled: false,
    customGcpServicesEnabled: false,
    secondaryTextLines: [],
    openCustomGcpServices: () => undefined,
    selectOverrideInstance: () => undefined,
};

const InstanceOverridesContext = createContext<InstanceOverridesContextValue>(EMPTY_CONTEXT);

export function useInstanceOverrides(): InstanceOverridesContextValue {
    return useContext(InstanceOverridesContext);
}

function getStandardInstance(
    availableInstances: EvoInstances,
    selectedInstance: EvoInstance,
): EvoInstances[number] | undefined {
    const matchingInstances = availableInstances.filter(
        ({ hub, org }) => org.id === selectedInstance.org?.id && !isInstanceOverrideCode(hub.code),
    );
    return (
        matchingInstances.find(({ hub }) => hub.url === selectedInstance.hub?.url) ??
        matchingInstances[0]
    );
}

function getValidationError(_: ServiceInstanceDefinition, value: string): string | undefined {
    return isValidCustomGcpServiceValue(value)
        ? undefined
        : 'Enter a lowercase deployment name or an approved Evo development base URL.';
}

function formatUrl(hubUrl: string, service: ServiceInstanceDefinition, value: string): string {
    const baseUrl = resolveCustomGcpServiceBaseUrl(value, hubUrl);
    return baseUrl ? `${baseUrl}${service.previewPath}` : value;
}

export function InstanceOverridesProvider({
    children,
    overrides,
    onInstanceChange,
    onConfigurationChanged,
}: Readonly<InstanceOverridesProviderProps>) {
    const [, refresh] = useReducer((revision) => revision + 1, 0);
    const [dialogInstance, setDialogInstance] = useState<EvoInstances[number]>();
    const customGcpDefinition = overrides.find(
        (
            override,
        ): override is Extract<InstanceOverrideDefinition, { type: 'custom-gcp-services' }> =>
            override.type === 'custom-gcp-services',
    );
    const currentInstance = getCurrentEvoInstance();
    const availableInstances = getAvailableEvoInstances();
    const standardInstance = getStandardInstance(availableInstances, currentInstance);
    const developmentEnvironment = getConfig().environment === 'development';
    const eligibleCustomGcpInstance =
        standardInstance && INSTANCE_OVERRIDE_DEV_HUB_CODES.includes(standardInstance.hub.code);
    const customGcpServicesEnabled =
        Boolean(customGcpDefinition) &&
        developmentEnvironment &&
        Boolean(eligibleCustomGcpInstance);
    const enabledInstanceOverrideKey = INSTANCE_OVERRIDE_IDS.filter((id) =>
        overrides.some(({ type }) => type === id),
    ).join(',');
    const enabled = customGcpServicesEnabled || Boolean(enabledInstanceOverrideKey);
    const serviceIds = customGcpDefinition?.serviceIds ?? [];

    useEffect(() => {
        setCustomGcpServicesEnabled(customGcpServicesEnabled);
        setEnabledInstanceOverrideIds(
            enabledInstanceOverrideKey.split(',').filter(isInstanceOverrideId),
        );
        refresh();

        return () => {
            setCustomGcpServicesEnabled(false);
            setEnabledInstanceOverrideIds([]);
        };
    }, [customGcpServicesEnabled, enabledInstanceOverrideKey]);

    useEffect(() => {
        window.addEventListener(EVO_INSTANCES_CHANGED_EVENT, refresh);
        return () => window.removeEventListener(EVO_INSTANCES_CHANGED_EVENT, refresh);
    }, []);

    const activeHubUrl = standardInstance?.hub.url ?? currentInstance.hub?.url;
    const secondaryTextLines =
        customGcpServicesEnabled && activeHubUrl
            ? getCustomGcpServiceDisplayLabels(activeHubUrl, serviceIds)
            : [];

    const selectOverrideInstance = (instance: EvoInstance) => {
        if (!instance.org || !instance.hub) return;
        setLastUsedInstance(instance.org.id, instance.hub.code);
        setCurrentEvoInstance({ org: instance.org, hub: instance.hub });
        onConfigurationChanged?.();
        onInstanceChange?.(instance);
        refresh();
    };

    const openCustomGcpServices = (instance?: EvoInstance) => {
        if (!customGcpServicesEnabled) return;
        const baseInstance = getStandardInstance(
            getAvailableEvoInstances(),
            instance ?? getCurrentEvoInstance(),
        );
        if (!baseInstance) return;

        setDialogInstance(baseInstance);
    };

    const handleApply = (nextValues: ServiceInstanceValues) => {
        if (!dialogInstance) return;
        const hubUrl = dialogInstance.hub.url;
        const currentValues = getCustomGcpServiceValues(hubUrl);
        const managedValues = Object.fromEntries(
            serviceIds.map((serviceId) => [serviceId, nextValues[serviceId] ?? '']),
        );
        if (!setCustomGcpServiceValues(hubUrl, { ...currentValues, ...managedValues })) return;

        setCustomGcpServicesDisplayLabel(getCustomGcpServiceDisplayLabels(hubUrl).join(', '));
        const customInstance = getAvailableEvoInstances().find(
            ({ hub, org }) =>
                hub.code === FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE &&
                org.id === dialogInstance.org.id &&
                hub.url === dialogInstance.hub.url,
        ) ?? {
            hub: {
                ...dialogInstance.hub,
                code: FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE,
                display_name: 'Custom GCP services',
            },
            org: {
                ...dialogInstance.org,
                display_name: '(Custom GCP services)',
            },
        };
        selectOverrideInstance(customInstance);
        setDialogInstance(undefined);
    };

    const handleReset = () => {
        if (!dialogInstance) return;
        const hubUrl = dialogInstance.hub.url;
        if (!clearCustomGcpServiceValuesFor(hubUrl, serviceIds)) return;

        setCustomGcpServicesDisplayLabel(getCustomGcpServiceDisplayLabels(hubUrl).join(', '));
        selectOverrideInstance(dialogInstance);
        setDialogInstance(undefined);
    };

    const values = dialogInstance ? getCustomGcpServiceValues(dialogInstance.hub.url) : {};

    return (
        <InstanceOverridesContext.Provider
            value={{
                enabled,
                customGcpServicesEnabled,
                secondaryTextLines,
                openCustomGcpServices,
                selectOverrideInstance,
            }}
        >
            {children}
            {customGcpServicesEnabled && dialogInstance && (
                <ServiceInstanceDialog
                    open
                    title="Custom GCP service connections"
                    description="These overrides affect only API requests made by this frontend in this browser. Backend-to-backend requests are not changed."
                    serviceIds={serviceIds}
                    values={values}
                    formatUrl={(service, value) =>
                        formatUrl(dialogInstance.hub.url, service, value)
                    }
                    validate={getValidationError}
                    onApply={handleApply}
                    onReset={handleReset}
                    onClose={() => setDialogInstance(undefined)}
                />
            )}
        </InstanceOverridesContext.Provider>
    );
}

export { SERVICE_INSTANCE_DEFINITIONS } from './ServiceInstanceDialog';
